// Markdown, in the amount a note is written in.
//
// A row's note and a comment on it are prose somebody types fast — during a call,
// from a transcript, or from an agent carrying a paragraph across from somewhere
// else. They were stored as markdown (see ItemRecord.note and llms.txt) and rendered
// as literal text, so `**the blocker**` came out with its asterisks and a list came
// out as one run-on line.
//
// **This produces an AST, never HTML.** A note can be written by a CLIENT from their
// own seat (`item.note` in worker/client-write.ts), so the rendered output lands in
// the team's session, and `dangerouslySetInnerHTML` over text a counterparty wrote
// is how that becomes their script running in your app. The renderer walks this tree
// into React elements instead; see components/Markdown.tsx.
//
// The subset is chosen, not partial: what a person writes in a note. Headings, lists,
// quotes, code, emphasis, links. No tables, no footnotes, no HTML passthrough, no
// reference links, and no nested lists — a note that needs a nested list is a
// document, and crum has somewhere else to put documents.
//
// Substrate-free like the rest of `lib/`: imports nothing, so `core.test.ts` covers it.

export type Block =
  | { type: 'p'; text: string }
  | { type: 'h'; level: 1 | 2 | 3; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; start: number; items: string[] }
  | { type: 'quote'; text: string }
  | { type: 'code'; lang: string; text: string }
  | { type: 'hr' }

const H = /^(#{1,3})\s+(.*)$/
const UL = /^[-*+]\s+(.*)$/
const OL = /^(\d{1,9})[.)]\s+(.*)$/
const QUOTE = /^>\s?(.*)$/
const HR = /^(?:---+|\*\*\*+|___+)$/
const FENCE = /^```\s*([A-Za-z0-9+#-]*)\s*$/

/**
 * Split a body into blocks.
 *
 * Line-based and single-pass. A blank line ends a paragraph; a list runs while its
 * marker does; a fence runs to its closing fence or to the end of the text, because
 * an unclosed fence in something half-typed should still render as code rather than
 * swallowing the parser.
 */
export function parseMarkdown(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, '\n').split('\n')
  const out: Block[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]!
    const trimmed = line.trim()

    if (!trimmed) {
      i += 1
      continue
    }

    const fence = FENCE.exec(trimmed)
    if (fence) {
      const body: string[] = []
      i += 1
      while (i < lines.length && !FENCE.test(lines[i]!.trim())) {
        body.push(lines[i]!)
        i += 1
      }
      i += 1 // the closing fence, or past the end
      out.push({ type: 'code', lang: fence[1] ?? '', text: body.join('\n') })
      continue
    }

    if (HR.test(trimmed)) {
      out.push({ type: 'hr' })
      i += 1
      continue
    }

    const heading = H.exec(trimmed)
    if (heading) {
      out.push({
        type: 'h',
        level: heading[1]!.length as 1 | 2 | 3,
        text: heading[2]!.trim(),
      })
      i += 1
      continue
    }

    if (UL.test(trimmed)) {
      const items: string[] = []
      while (i < lines.length) {
        const m = UL.exec(lines[i]!.trim())
        if (!m) break
        items.push(m[1]!)
        i += 1
      }
      out.push({ type: 'ul', items })
      continue
    }

    const ordered = OL.exec(trimmed)
    if (ordered) {
      const items: string[] = []
      const start = Number(ordered[1])
      while (i < lines.length) {
        const m = OL.exec(lines[i]!.trim())
        if (!m) break
        items.push(m[2]!)
        i += 1
      }
      out.push({ type: 'ol', start: Number.isFinite(start) ? start : 1, items })
      continue
    }

    if (QUOTE.test(trimmed)) {
      const body: string[] = []
      while (i < lines.length) {
        const m = QUOTE.exec(lines[i]!.trim())
        if (!m) break
        body.push(m[1]!)
        i += 1
      }
      out.push({ type: 'quote', text: body.join('\n') })
      continue
    }

    // A paragraph runs to the next blank line or the next block opener. Its own
    // newlines are kept: somebody who pressed return meant it.
    const body: string[] = [line.trim()]
    i += 1
    while (i < lines.length) {
      const next = lines[i]!.trim()
      if (!next || opensABlock(next)) break
      body.push(next)
      i += 1
    }
    out.push({ type: 'p', text: body.join('\n') })
  }

  return out
}

function opensABlock(line: string): boolean {
  return (
    H.test(line) ||
    UL.test(line) ||
    OL.test(line) ||
    QUOTE.test(line) ||
    HR.test(line) ||
    FENCE.test(line)
  )
}

export type Inline =
  | { type: 'text'; text: string }
  | { type: 'strong'; text: string }
  | { type: 'em'; text: string }
  | { type: 'del'; text: string }
  | { type: 'code'; text: string }
  | { type: 'link'; text: string; href: string }
  | { type: 'mention'; id: string; label: string }

// A mention is `@[Label](id)` — link-shaped on purpose, so it is matched FIRST and
// the link rule below never gets to eat one. See lib/mentions.ts.
const INLINE =
  /@\[([^\]\n]+)\]\(([a-z]+_[0-9A-Za-z_-]+)\)|`([^`\n]+)`|\*\*([^*\n]+)\*\*|__([^_\n]+)__|~~([^~\n]+)~~|\*([^*\n]+)\*|(?<![A-Za-z0-9])_([^_\n]+)_(?![A-Za-z0-9])|\[([^\]\n]*)\]\(([^)\s]+)\)|(https?:\/\/[^\s<>()]+)/g

/**
 * One pass over a run of text.
 *
 * Deliberately flat: emphasis inside a link, or a link inside bold, renders as the
 * outer thing with plain text inside. Notes do not need the nesting and a nesting
 * parser is where a hand-written one starts getting things subtly wrong.
 */
export function parseInline(src: string): Inline[] {
  const out: Inline[] = []
  let last = 0
  INLINE.lastIndex = 0
  let m: RegExpExecArray | null

  while ((m = INLINE.exec(src)) !== null) {
    if (m.index > last) out.push({ type: 'text', text: src.slice(last, m.index) })
    if (m[1] !== undefined) out.push({ type: 'mention', label: m[1], id: m[2]! })
    else if (m[3] !== undefined) out.push({ type: 'code', text: m[3] })
    else if (m[4] !== undefined) out.push({ type: 'strong', text: m[4] })
    else if (m[5] !== undefined) out.push({ type: 'strong', text: m[5] })
    else if (m[6] !== undefined) out.push({ type: 'del', text: m[6] })
    else if (m[7] !== undefined) out.push({ type: 'em', text: m[7] })
    else if (m[8] !== undefined) out.push({ type: 'em', text: m[8] })
    else if (m[9] !== undefined) {
      const href = safeHref(m[10] ?? '')
      if (href) out.push({ type: 'link', text: m[9] || href, href })
      else out.push({ type: 'text', text: m[0] })
    } else if (m[11] !== undefined) out.push({ type: 'link', text: m[11], href: m[11] })
    last = m.index + m[0].length
  }
  if (last < src.length) out.push({ type: 'text', text: src.slice(last) })
  return out
}

/**
 * Only schemes that navigate. `javascript:` in a link a client typed is the one way
 * text in this app could become code in somebody else's session, and a relative href
 * would send them somewhere inside crum wearing a label that says otherwise.
 */
export function safeHref(href: string): string | null {
  const url = href.trim()
  return /^(https?:\/\/|mailto:)/i.test(url) ? url : null
}

/** Does this body use any markdown at all? For deciding whether to say so in a hint. */
export function looksLikeMarkdown(src: string): boolean {
  return parseMarkdown(src).some((b) => b.type !== 'p') || /[*_`~]|\[[^\]]*\]\(/.test(src)
}
